import { useState, useEffect, useCallback } from 'react'
import { Check, X, Loader2, Plus } from 'lucide-react'
import { useEquipmentSpecifications, SpecWithTemplate, SpecificationTemplate, normalizeReferenceIds, isUuid } from '@/hooks/useEquipmentSpecifications'
import { supabase } from '@/lib/supabase/client'
import EditableEquipmentDetails from './EditableEquipmentDetails'
import LxBxHInput from './LxBxHInput'
import ManufacturerSearchField from './ManufacturerSearchField'

interface IdentityEquipment {
  id: string
  name: string
  part_number: string | null
  asset_number: string
  serial_number: string | null
  owner_id: string | null
  last_inspection_date: string | null
  next_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  status: string
  inspection_status: string | null
  description: string | null
  model: string | null
  manufacture_year: number | null
  category_id: string | null
}

interface SpecificationsPanelProps {
  equipmentId: string
  categoryId: string | null
  onSpecsUpdated?: (change?: { specKey: string; value: any }) => void
  identityEquipment?: IdentityEquipment
  onIdentityUpdate?: (updated: IdentityEquipment) => void
  /** 'stmidb' for SLB equipment (specs and identity live outside equipment_v2). */
  source?: 'stmidb'
}

interface ReferenceOptions {
  standards: Array<{ value: string; label: string }>
}

const SpecificationsPanel = ({
  equipmentId,
  categoryId,
  onSpecsUpdated,
  identityEquipment,
  onIdentityUpdate,
  source,
}: SpecificationsPanelProps) => {
  const {
    specsWithTemplates,
    loading,
    saving,
    error,
    saveSpecification,
    manufacturers: resolvedManufacturers,
    standards: resolvedStandards,
  } = useEquipmentSpecifications({ equipmentId, categoryId, source })

  // Tap-to-edit state — only one chip editable at a time
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState<any>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showAddStandard, setShowAddStandard] = useState(false)

  const [referenceOptions, setReferenceOptions] = useState<ReferenceOptions>({
    standards: [],
  })
  const [loadingRefs, setLoadingRefs] = useState(false)

  useEffect(() => {
    const loadReferenceOptions = async () => {
      setLoadingRefs(true)
      try {
        let standardsQuery = supabase
          .from('standards_v2')
          .select('id, code, title')
          .eq('is_active', true)

        if (categoryId) {
          const { data: categoryStandards } = await supabase
            .from('standards_categories')
            .select('standard_id')
            .eq('category_id', categoryId)

          if (categoryStandards && categoryStandards.length > 0) {
            standardsQuery = standardsQuery.in(
              'id',
              categoryStandards.map(sc => sc.standard_id),
            )
          }
        }

        const { data: stdData } = await standardsQuery.order('code', { ascending: true })

        setReferenceOptions({
          standards: stdData?.map(s => ({ value: s.code, label: s.code })) || [],
        })
      } catch { /* silent */ } finally {
        setLoadingRefs(false)
      }
    }

    loadReferenceOptions()
  }, [categoryId])

  // Reset edit state if specs reload (e.g. category change)
  useEffect(() => {
    setEditingKey(null)
    setEditingValue(null)
    setSaveError(null)
  }, [equipmentId, categoryId])

  const startEdit = (specKey: string, rawValue: any) => {
    setEditingKey(specKey)
    if (specKey === 'prod_acc_to') {
      setEditingValue(Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [])
    } else {
      setEditingValue(rawValue ?? '')
    }
    setShowAddStandard(false)
    setSaveError(null)
  }

  const cancelEdit = () => {
    setEditingKey(null)
    setEditingValue(null)
    setShowAddStandard(false)
    setSaveError(null)
  }

  const handleQuickSave = useCallback(
    async (template: SpecificationTemplate, value: any) => {
      const success = await saveSpecification(template, value)
      if (success) onSpecsUpdated?.({ specKey: template.spec_key, value })
      return success
    },
    [saveSpecification, onSpecsUpdated],
  )

  const saveSpec = async () => {
    if (!editingKey) return
    const item = specsWithTemplates.find(s => s.template.spec_key === editingKey)
    if (!item) return

    // Treat empty string / empty array as null so the field is cleared
    const valueToSave =
      editingValue === '' || editingValue === undefined ||
      (Array.isArray(editingValue) && editingValue.length === 0)
        ? null
        : editingValue

    setSavingKey(editingKey)
    const success = await handleQuickSave(item.template, valueToSave)
    setSavingKey(null)

    if (success) {
      cancelEdit()
    } else {
      setSaveError('Kunne ikke lagre')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); saveSpec() }
    if (e.key === 'Escape') cancelEdit()
  }

  const renderSpec = (sw: SpecWithTemplate) => {
    const { template, displayValue, rawValue, spec } = sw
    const label = template.label_no || template.label_en || template.spec_key
    const isActive = editingKey === template.spec_key
    const isSaving = savingKey === template.spec_key
    const isManufacturer = ['manufacturer', 'manufacturer_id', 'produsent'].includes(template.spec_key)
    const isStandards = template.spec_key === 'prod_acc_to'
    const isBoolean = template.data_type === 'boolean'
    const isEnum = template.data_type === 'enum' && template.enum_options && template.enum_options.length > 0
    const isDate = template.data_type === 'date'
    const isReference = template.data_type === 'reference' || isManufacturer || isStandards
    const isDimensions = template.spec_key === 'dimensions'

    // ── Boolean: immediate toggle, no edit state ────────────────────────────
    if (isBoolean) {
      const boolVal = rawValue === true || rawValue === 'true'
      return (
        <button
          key={template.spec_key}
          onClick={async () => {
            setSavingKey(template.spec_key)
            await handleQuickSave(template, !boolVal)
            setSavingKey(null)
          }}
          disabled={!!savingKey}
          className={`bg-white border rounded-md px-3 py-2 text-left transition-colors w-full ${
            boolVal ? 'border-emerald-300' : 'border-slate-200 hover:border-[#1E3A5F]'
          }`}
        >
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 truncate leading-none mb-1">
            {label}
          </p>
          <p className={`text-sm font-semibold ${boolVal ? 'text-emerald-600' : 'text-slate-400'}`}>
            {isSaving ? <Loader2 size={13} className="animate-spin" /> : (boolVal ? 'Ja' : 'Nei')}
          </p>
        </button>
      )
    }

    // ── Standards: tag list + "+" to add more ──────────────────────────────
    if (isStandards && isActive) {
      const currentIds: string[] = Array.isArray(editingValue) ? editingValue : []
      const available = referenceOptions.standards.filter(
        o => !currentIds.includes(o.value) && !currentIds.includes(o.label),
      )

      return (
        <div key={template.spec_key} className="bg-white border border-[#1E3A5F] shadow-sm rounded-md px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 truncate leading-none mb-1.5">
            {label}
          </p>

          <div className="flex flex-wrap gap-1 mb-1.5">
            {currentIds.map(id => {
              const opt = referenceOptions.standards.find(o => o.value === id || o.label === id)
              const labelText = opt?.label || resolvedStandards[id]?.code || (!isUuid(id) ? id : null)
              return labelText ? (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded"
                >
                  {labelText}
                  <button
                    onClick={() => setEditingValue(currentIds.filter(i => i !== id))}
                    className="text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    <X size={10} />
                  </button>
                </span>
              ) : null
            })}

            {/* Add button */}
            {available.length > 0 && !showAddStandard && (
              <button
                onClick={() => setShowAddStandard(true)}
                className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs font-medium text-[#1E3A5F] border border-dashed border-[#1E3A5F] rounded hover:bg-slate-50 transition-colors"
              >
                <Plus size={10} />
                Legg til
              </button>
            )}

            {/* Inline add select */}
            {showAddStandard && (
              <select
                autoFocus
                defaultValue=""
                onChange={e => {
                  if (e.target.value) {
                    setEditingValue([...currentIds, e.target.value])
                    setShowAddStandard(false)
                  }
                }}
                onBlur={() => setShowAddStandard(false)}
                className="text-xs text-slate-800 border-b border-[#1E3A5F] bg-transparent focus:outline-none py-0.5"
              >
                <option value="">Velg standard...</option>
                {available.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
          </div>

          {saveError && <p className="text-xs text-red-600 mb-1">{saveError}</p>}
          <div className="flex gap-1.5">
            <button
              onClick={saveSpec}
              disabled={isSaving}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-[#1E3A5F] rounded hover:bg-[#152C4A] transition-colors disabled:opacity-50"
            >
              {isSaving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              Lagre
            </button>
            <button
              onClick={cancelEdit}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors"
            >
              <X size={11} />
              Avbryt
            </button>
          </div>
        </div>
      )
    }

    // ── Active edit chip (all other types) ─────────────────────────────────
    if (isActive) {
      const inputBase =
        'w-full text-sm text-slate-800 bg-transparent focus:outline-none border-b border-slate-200 focus:border-[#1E3A5F] py-0.5 transition-colors'

      let inputEl: React.ReactNode

      if (isManufacturer) {
        inputEl = (
          <ManufacturerSearchField
            variant="chip"
            autoFocus
            value={editingValue || ''}
            onChange={setEditingValue}
          />
        )
      } else if (isReference && template.reference_table) {
        inputEl = (
          <select
            autoFocus
            value={editingValue || ''}
            onChange={e => setEditingValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loadingRefs}
            className={inputBase}
          >
            <option value="">Velg...</option>
            {referenceOptions.standards.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )
      } else if (isEnum) {
        inputEl = (
          <select
            autoFocus
            value={editingValue || ''}
            onChange={e => setEditingValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={inputBase}
          >
            <option value="">Velg...</option>
            {template.enum_options!.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        )
      } else if (isDimensions) {
        inputEl = (
          <LxBxHInput
            autoFocus
            variant="chip"
            value={editingValue ?? ''}
            onChange={setEditingValue}
            onKeyDown={handleKeyDown}
          />
        )
      } else if (isDate) {
        inputEl = (
          <input
            autoFocus
            type="date"
            value={editingValue || ''}
            onChange={e => setEditingValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={inputBase}
          />
        )
      } else {
        inputEl = (
          <input
            autoFocus
            type="text"
            inputMode={
              template.data_type === 'integer' || template.data_type === 'year'
                ? 'numeric'
                : ['numeric', 'decimal', 'angle', 'currency', 'percentage'].includes(template.data_type)
                ? 'decimal'
                : 'text'
            }
            value={editingValue ?? ''}
            onChange={e => setEditingValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={spec?.unit?.symbol ? `0 ${spec.unit.symbol}` : ''}
            className={inputBase}
          />
        )
      }

      return (
        <div
          key={template.spec_key}
          className={`bg-white border border-[#1E3A5F] shadow-sm rounded-md px-3 py-2 ${
            isDimensions ? 'col-span-3' : ''
          }`}
        >
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 truncate leading-none mb-1.5">
            {label}
            {spec?.unit?.symbol && (
              <span className="normal-case tracking-normal ml-1 text-slate-300">
                ({spec.unit.symbol})
              </span>
            )}
          </p>
          {inputEl}
          {saveError && <p className="text-xs text-red-600 mt-1">{saveError}</p>}
          <div className="flex gap-1.5 mt-2">
            <button
              onClick={saveSpec}
              disabled={isSaving}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-[#1E3A5F] rounded hover:bg-[#152C4A] transition-colors disabled:opacity-50"
            >
              {isSaving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              Lagre
            </button>
            <button
              onClick={cancelEdit}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors"
            >
              <X size={11} />
              Avbryt
            </button>
          </div>
        </div>
      )
    }

    // ── Read-only chip (tappable) ────────────────────────────────────────────
    // For standards, resolve the display from the same fully-loaded options
    // list that edit mode uses (with the hook cache as fallback) so the value
    // shows immediately without having to open the field.
    const readOnlyText = isStandards
      ? (Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [])
          .flatMap((id: string) => normalizeReferenceIds(id))
          .map((id: string) =>
            referenceOptions.standards.find(o => o.value === id || o.label === id)?.label ||
            resolvedStandards[id]?.code ||
            (isUuid(id) ? null : id),
          )
          .filter(Boolean)
          .join(', ')
        || displayValue
      : displayValue

    return (
      <div
        key={template.spec_key}
        onClick={() => startEdit(template.spec_key, rawValue)}
        className={`bg-white border border-slate-200 rounded-md px-3 py-2 min-w-0 cursor-pointer group hover:border-[#1E3A5F] transition-colors ${
          isDimensions ? 'col-span-2' : ''
        }`}
      >
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 truncate leading-none mb-1">
          {label}
          {spec?.unit?.symbol && (
            <span className="normal-case tracking-normal ml-1 text-slate-300">
              ({spec.unit.symbol})
            </span>
          )}
        </p>
        <p className="text-sm font-semibold text-slate-800 group-hover:text-[#1E3A5F] truncate leading-snug transition-colors">
          {readOnlyText || <span className="text-slate-300 font-normal">—</span>}
        </p>
      </div>
    )
  }

  if (loading && !identityEquipment) {
    return (
      <div className="flex items-center gap-2 py-6 text-slate-400 text-sm">
        <div className="w-4 h-4 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
        Laster spesifikasjoner...
      </div>
    )
  }

  if (specsWithTemplates.length === 0 && !identityEquipment) return null

  return (
    <section>
      <div className="flex items-center gap-3 mb-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
          Tekniske spesifikasjoner
        </p>
        <div className="flex-1 h-px bg-slate-100" />
      </div>

      {identityEquipment && (
        <div className="mb-3">
          <EditableEquipmentDetails
            equipment={identityEquipment}
            onUpdate={onIdentityUpdate}
            source={source}
          />
        </div>
      )}

      {error && (
        <div className="mb-3 px-4 py-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-slate-400 text-sm">
          <div className="w-4 h-4 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
          Laster spesifikasjoner...
        </div>
      ) : specsWithTemplates.length > 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
          {(() => {
            const isManufacturer = (sw: SpecWithTemplate) =>
              ['manufacturer', 'manufacturer_id', 'produsent'].includes(sw.template.spec_key)
            const isProdYear = (sw: SpecWithTemplate) => {
              const key = sw.template.spec_key.toLowerCase()
              if (['prod_year', 'manufacture_year', 'prod_år'].includes(key)) return true
              return /prod\.?\s*år/i.test(sw.template.label_no || sw.template.label_en || '')
            }
            const isNdt = (sw: SpecWithTemplate) =>
              ['ndt_date', 'last_ndt_date'].includes(sw.template.spec_key)
            const isCertificate = (sw: SpecWithTemplate) => {
              const key = sw.template.spec_key.toLowerCase()
              if (['certificate', 'cert_no', 'certificate_no', 'sertifikat'].includes(key)) return true
              return /sertifikat|certificate/i.test(sw.template.label_no || sw.template.label_en || '')
            }
            const manufacturer = specsWithTemplates.find(isManufacturer)
            const prodYear = specsWithTemplates.find(isProdYear)
            const ndt = specsWithTemplates.find(isNdt)
            const certificate = specsWithTemplates.find(isCertificate)
            const rest = specsWithTemplates.filter(
              sw => sw !== manufacturer && sw !== prodYear && sw !== ndt && sw !== certificate,
            )
            return (
              <>
                {(ndt || certificate) && (
                  <div className="grid grid-cols-2 gap-2">
                    {ndt && renderSpec(ndt)}
                    {certificate && renderSpec(certificate)}
                  </div>
                )}
                {(manufacturer || prodYear) && (
                  <div className="grid grid-cols-2 gap-2">
                    {manufacturer && renderSpec(manufacturer)}
                    {prodYear && renderSpec(prodYear)}
                  </div>
                )}
                {rest.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {rest.map(sw => renderSpec(sw))}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      ) : null}
    </section>
  )
}

export default SpecificationsPanel
